import type { RequestInfo } from '@warp-drive/core/types/request';
import { SkipCache } from '@warp-drive/core-types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { buildQueryRequestUrl } from '../utils/request-url.ts';
import { buildTioAuthHeaders } from '../utils/auth-headers.ts';
import downloadFile from '../utils/download-file.ts';
import type {
  ReportExportResponse,
  ExportAssetOptions,
  ExportAssetResponse,
} from '../data/resources/search.ext';

/**
 * Request Builders for OpenSearch Operations
 *
 * These builders provide typed access to search, mapping, stats,
 * and other OpenSearch-related endpoints.
 *
 * For OpenSearch response types (OpenSearchHit, OpenSearchHits, OpenSearchResponse),
 * import from 'tio-data/data/resources/search.ext' to avoid duplication.
 *
 * WAVE 1.5 (search-requests): the JSON:API builders below replace the
 * `store.request`-routable methods on the byte-identical
 * `tio-employee/app/adapters/search.ts` + `tio-admin/app/adapters/search.ts`.
 * The two BLOB downloads (`search.csv`, `reports/{id}/export`) bypass JSON:API
 * content negotiation, so they are raw-`fetch` helpers (NOT `store.request`)
 * using `buildTioAuthHeaders` — the established Phase 2.x download pattern. The
 * legacy adapters built these via `this.buildURL('search') + '.csv'` and
 * `${host}/${namespace}/reports/${id}/export`; the helpers reproduce those
 * exact URLs + the adapter's header bundle + the inline downloadFile trigger.
 *
 * Admin-only methods (`explain`, `rebuild*`/`reindex`, `indexByCompany`) are
 * pre-existing or deferred — see TODO(wave 3 — admin) below; do NOT route the
 * admin app's call sites through here until wave 3.
 */

const searchUrl = (path?: string) => buildQueryRequestUrl('search', 'api/v2/search', path);
const adminUrl = (path: string) => buildQueryRequestUrl('admin', 'api/v2/admin', path);

/**
 * Build the `model-type` + optional `company-id` / `program-template-id` query
 * string shared by the `fields` / `mapping?include-label-maps` / `key-label-maps`
 * / `employee-custom-field-mappings` metadata GETs. Param ORDER + dasherized
 * keys are preserved EXACTLY from the legacy adapters' `URLSearchParams`
 * construction so the emitted query strings stay byte-identical.
 */
function metadataParams(
  base: Record<string, string>,
  companyId?: string,
  programTemplateId?: string
): string {
  const params = new URLSearchParams(base);
  if (companyId) {
    params.set('company-id', companyId);
  }
  if (programTemplateId) {
    params.set('program-template-id', programTemplateId);
  }
  return params.toString();
}

/**
 * Response types for search endpoints
 */
export interface SearchMappingResponse {
  [key: string]: unknown;
}

export interface SearchStatsResponse {
  index: string;
  docCount: number;
  size: string;
  sizeInBytes: number;
  status: 'healthy' | 'warning' | 'critical';
  rawStats: {
    health: Record<string, unknown>;
    stats: Record<string, unknown>;
  };
}

export interface SearchSettingsResponse {
  [key: string]: unknown;
}

export interface SearchExplainResponse {
  [key: string]: unknown;
}

export interface FieldAnalysis {
  field: string;
  type: string;
  sampleValue?: unknown;
  currentType?: string;
  newType?: string;
}

export interface SearchDataPreviewResponse {
  recordType: string;
  recordId: string;
  searchData: Record<string, unknown>;
  analysis: {
    newFields: FieldAnalysis[];
    existingFields: FieldAnalysis[];
    typeConflicts: FieldAnalysis[];
    totalFields: number;
    currentMappingFieldCount: number;
  };
}

/**
 * Get the OpenSearch mapping for a model type
 *
 * @param modelType - The model type (Person, PslfForm, TasApplication)
 *
 * @example
 * const { content } = await store.request(getSearchMappingRequest('Person'));
 */
export function getSearchMappingRequest(modelType: string): RequestInfo {
  return {
    url: searchUrl(`mapping?model-type=${modelType}`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Get OpenSearch index statistics for a model type
 *
 * @param modelType - The model type (Person, PslfForm, TasApplication)
 *
 * @example
 * const { content } = await store.request(getSearchStatsRequest('Person'));
 */
export function getSearchStatsRequest(modelType: string): RequestInfo {
  return {
    url: searchUrl(`stats?model-type=${modelType}`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Get OpenSearch index settings for a model type
 *
 * @param modelType - The model type (Person, PslfForm, TasApplication)
 *
 * @example
 * const { content } = await store.request(getSearchSettingsRequest('Person'));
 */
export function getSearchSettingsRequest(modelType: string): RequestInfo {
  return {
    url: searchUrl(`settings?model-type=${modelType}`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Get the OpenSearch mapping for a model type WITH key-label maps merged in.
 *
 * GET /api/v2/search/mapping?model-type=<type>&include-label-maps=true[&company-id][&program-template-id]
 *
 * Mirrors `SearchModelAdapter#mappingWithLabelMaps` in both apps (byte-identical):
 * `this.buildURL('search') + '/mapping?' + params` with `params` =
 * `{ 'model-type', 'include-label-maps': 'true' }` then optional `company-id`,
 * `program-template-id` (that param ORDER preserved here).
 */
export function getSearchMappingWithLabelMapsRequest(
  modelType: string,
  companyId?: string,
  programTemplateId?: string
): RequestInfo {
  const qs = metadataParams(
    { 'model-type': modelType, 'include-label-maps': 'true' },
    companyId,
    programTemplateId
  );
  return {
    url: searchUrl(`mapping?${qs}`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Get the report-builder field catalog for a model type.
 *
 * GET /api/v2/search/fields?model-type=<type>[&company-id][&program-template-id]
 *
 * Mirrors `SearchModelAdapter#fields` in both apps (byte-identical):
 * `this.buildURL('search') + '/fields?' + params` with `params` =
 * `{ 'model-type' }` then optional `company-id`, `program-template-id`.
 */
export function getSearchFieldsRequest(
  modelType: string,
  companyId?: string,
  programTemplateId?: string
): RequestInfo {
  const qs = metadataParams({ 'model-type': modelType }, companyId, programTemplateId);
  return {
    url: searchUrl(`fields?${qs}`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * List the available Searchkick indexes (model types) with display metadata.
 *
 * GET /api/v2/search/indexes
 *
 * Mirrors `SearchModelAdapter#listIndexes` (admin adapter; shared builder for
 * forward use): `this.buildURL('search') + '/indexes'`.
 */
export function getSearchIndexesRequest(): RequestInfo {
  return {
    url: searchUrl('indexes'),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * List ALL indexes in the OpenSearch cluster (not just aliases).
 *
 * GET /api/v2/search/cluster-indexes
 *
 * Mirrors `SearchModelAdapter#clusterIndexes` (admin adapter; shared builder for
 * forward use): `this.buildURL('search') + '/cluster-indexes'`.
 */
export function getClusterIndexesRequest(): RequestInfo {
  return {
    url: searchUrl('cluster-indexes'),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Execute an OpenSearch query
 *
 * @param query - The search query object
 *
 * @example
 * const { content } = await store.request(executeSearchRequest({
 *   filter: { 'model-type': 'Person' },
 *   query: { match_all: {} }
 * }));
 */
export function executeSearchRequest(query: Record<string, unknown>): RequestInfo {
  return {
    url: searchUrl(),
    method: 'POST',
    body: JSON.stringify(query),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Explain how a search query would execute
 *
 * @param query - The search query object
 *
 * @example
 * const { content } = await store.request(explainSearchRequest({
 *   filter: { 'model-type': 'Person' },
 *   query: { match_all: {} }
 * }));
 */
export function explainSearchRequest(query: Record<string, unknown>): RequestInfo {
  return {
    url: searchUrl('explain'),
    method: 'POST',
    body: JSON.stringify(query),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Preview what search_data would look like for a record and compare to current mapping
 *
 * @param modelType - The model type (Person, PslfForm, TasApplication)
 * @param recordId - The ID of the record to preview
 *
 * @example
 * const { content } = await store.request(previewSearchDataRequest('Person', '172654'));
 * const preview = content as SearchDataPreviewResponse;
 * console.log(`New fields: ${preview.analysis.newFields.length}`);
 * console.log(`Type conflicts: ${preview.analysis.typeConflicts.length}`);
 */
export function previewSearchDataRequest(modelType: string, recordId: string): RequestInfo {
  return {
    url: searchUrl(`preview?model-type=${modelType}&record-id=${recordId}`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Trigger a full reindex for a model type
 *
 * @param type - The model type to reindex
 *
 * @example
 * const { content } = await store.request(rebuildIndexRequest('Person'));
 */
export function rebuildIndexRequest(type: string): RequestInfo {
  return {
    url: adminUrl(`reindex?type=${type}`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Reindex a specific record
 *
 * @param modelType - The model type
 * @param recordId - The ID of the record to reindex
 *
 * @example
 * const { content } = await store.request(rebuildRecordIndexRequest('Person', '123'));
 */
export function rebuildRecordIndexRequest(modelType: string, recordId: string): RequestInfo {
  return {
    url: adminUrl('reindex-record'),
    method: 'POST',
    body: JSON.stringify({
      model_type: modelType,
      record_id: recordId,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Update existing index documents company-by-company (in-place, unlike a full
 * reindex). Admin-only surface.
 *
 * POST /admin/index-by-company  body `{ model_type, company_ids, active_only }`
 *
 * Mirrors `SearchModelAdapter#indexByCompany` (tio-admin adapter only), which
 * built the URL via `this.buildURL() + '/admin/index-by-company'` and sent
 * `this.ajax(url, 'POST', { data: { model_type: type, company_ids:
 * options?.companyIds, active_only: options?.activeOnly ?? false } })`. The
 * snake_case keys + the `active_only` default-of-`false` are preserved exactly;
 * `company_ids` is emitted as-passed (the adapter sent `undefined` through to
 * the body when omitted, which `JSON.stringify` drops — matched here).
 */
export function indexByCompanyRequest(
  type: string,
  options?: { companyIds?: number[]; activeOnly?: boolean }
): RequestInfo {
  return {
    url: adminUrl('index-by-company'),
    method: 'POST',
    body: JSON.stringify({
      model_type: type,
      company_ids: options?.companyIds,
      active_only: options?.activeOnly ?? false,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/* ------------------------------------------------------------------------- *
 * BLOB downloads — raw fetch helpers (NOT store.request).
 *
 * `search.csv` (CSV stream) and `reports/{id}/export` (JSON envelope pointing
 * at a signed S3 URL) bypass JSON:API content negotiation, so they cannot ride
 * the `store.request` builder pipeline. They mirror the established Phase 2.x
 * raw-fetch download pattern: `buildTioAuthHeaders` reproduces the adapter's
 * `headers` bundle (x-api-key from the caller's per-app ENV, x-auth-key /
 * tio-auth-token / Time-Zone / X-Request-Id from auth-context) and the helper
 * triggers `downloadFile` inline exactly as the adapters did.
 * ------------------------------------------------------------------------- */

/** Build the `search.csv` URL: `{host}/api/v2/search.csv`. Mirrors the legacy
 * adapter's `this.buildURL('search') + '.csv'`. */
export function searchCsvUrl(): string {
  return `${searchUrl()}.csv`;
}

/** Build the report-export URL: `{host}/api/v2/reports/{id}/export`. Mirrors the
 * legacy adapter's `${this.host}/${this.namespace}/reports/${reportId}/export`. */
export function reportExportUrl(reportId: string): string {
  const reportsBase = buildBaseURL({
    identifier: { type: 'report' },
    op: 'query',
    resourcePath: 'api/v2/reports',
  } satisfies QueryUrlOptions);
  return `${reportsBase}/${reportId}/export`;
}

/** Build the search export-asset URL: `{host}/api/v2/search/export-asset`. Mirrors
 * the legacy adapter's `this.buildURL('search') + '/export-asset'`. */
export function searchExportAssetUrl(): string {
  return searchUrl('export-asset');
}

/**
 * POST /api/v2/search.csv — stream the OpenSearch result set as CSV and trigger
 * a browser download. Replaces `SearchModelAdapter#downloadCsv` (both apps;
 * preserves the admin superset behaviour: `columnLabels` is a frontend-only
 * concern stripped from the request body and applied to the CSV header row —
 * the employee callers never pass it, so for them the strip is a no-op and the
 * wire body is unchanged).
 *
 * Headers match the adapter EXACTLY: the full `headers` bundle with
 * `Content-Type: application/json` (NB: no trailing `;` — the adapter
 * overrode `'application/json;'` with `'application/json'` here) and
 * `Accept: text/csv`.
 *
 * @param query  the search body (may carry a frontend-only `columnLabels` map)
 * @param apiKey the per-app `ENV.apiKey` (tio-data is owner-free — see auth-headers)
 */
export async function downloadSearchCsv(
  query: Record<string, unknown>,
  { apiKey }: { apiKey: string }
): Promise<void> {
  // Extract columnLabels before sending to backend (frontend-only concern).
  const { columnLabels, ...requestPayload } = query as {
    columnLabels?: Record<string, string>;
    [key: string]: unknown;
  };

  const headers = buildTioAuthHeaders({
    apiKey,
    contentType: 'application/json', // sending JSON body
    accept: 'text/csv', // expect CSV response
  });

  const response = await fetch(searchCsvUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify(requestPayload),
  });

  if (response.ok) {
    const contentDisposition = response.headers.get('Content-Disposition');
    const name = contentDisposition?.split('filename=')[1]?.split('.')[0];
    const extension = contentDisposition?.split('.')[1]?.split(';')[0];
    const filename = `${name}.${extension}`.replaceAll('"', '');

    let blob = await response.blob();

    // Apply custom column labels to CSV header row (admin superset behaviour).
    if (columnLabels && Object.keys(columnLabels).length > 0) {
      const text = await blob.text();
      const lineBreak = text.indexOf('\r\n') !== -1 ? '\r\n' : '\n';
      const lines = text.split(lineBreak);
      if (lines[0]) {
        // Parse CSV header respecting quoted fields
        const headerCells: string[] = [];
        let current = '';
        let inQuotes = false;
        for (const ch of lines[0]!) {
          if (ch === '"') {
            inQuotes = !inQuotes;
            current += ch;
          } else if (ch === ',' && !inQuotes) {
            headerCells.push(current);
            current = '';
          } else {
            current += ch;
          }
        }
        headerCells.push(current);

        lines[0] = headerCells
          .map((h) => {
            const cleaned = h.replace(/^"|"$/g, '');
            const label = columnLabels[cleaned];
            if (label) {
              // Escape double-quotes per CSV spec (RFC 4180)
              const escaped = label.replace(/"/g, '""');
              return `"${escaped}"`;
            }
            return h;
          })
          .join(',');
        blob = new Blob([lines.join(lineBreak)], { type: 'text/csv' });
      }
    }

    downloadFile(blob, filename, 'text/csv');
  }
}

/**
 * POST /api/v2/reports/{id}/export — ask the API to render a saved report to a
 * signed S3 CSV, then download the file from the returned signed URL. Replaces
 * `SearchModelAdapter#exportReport` (both apps; byte-identical). The body
 * snake_cases each provided option (`company_id`, `search_body`, `model_type`,
 * `column_keys`, `column_labels`, `column_formats`) and omits absent keys,
 * preserving the legacy spread-conditional shape exactly.
 *
 * @param reportId the saved report id
 * @param options  optional export overrides + the per-app `ENV.apiKey`
 */
export async function exportReport(
  reportId: string,
  options: {
    apiKey: string;
    companyId?: string;
    searchBody?: Record<string, unknown>;
    modelType?: string;
    columnKeys?: string[];
    columnLabels?: Record<string, string>;
    columnFormats?: Record<string, unknown>;
  }
): Promise<ReportExportResponse> {
  const headers = buildTioAuthHeaders({
    apiKey: options.apiKey,
    contentType: 'application/json',
  });

  const response = await fetch(reportExportUrl(reportId), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      ...(options.companyId ? { company_id: options.companyId } : {}),
      ...(options.searchBody ? { search_body: options.searchBody } : {}),
      ...(options.modelType ? { model_type: options.modelType } : {}),
      ...(options.columnKeys ? { column_keys: options.columnKeys } : {}),
      ...(options.columnLabels ? { column_labels: options.columnLabels } : {}),
      ...(options.columnFormats ? { column_formats: options.columnFormats } : {}),
    }),
  });

  if (!response.ok) {
    const body = await response.json();
    throw new Error(body.error || `Export failed (${response.status})`);
  }

  const result = (await response.json()) as ReportExportResponse;

  // Trigger download from the signed URL
  const fileResponse = await fetch(result.signed_url);
  if (fileResponse.ok) {
    const blob = await fileResponse.blob();
    downloadFile(blob, result.filename, 'text/csv');
  }

  return result;
}

/**
 * POST /api/v2/search/export-asset — render the OpenSearch result set to a
 * signed S3 CSV through the policied search pipeline (same SearchPolicy
 * authorization as preview search, so the export is scoped exactly like the
 * data on screen), then download the file from the returned signed asset URL.
 * Replaces `SearchModelAdapter#exportAsset` (both apps; byte-identical body).
 * `model_type` + `search_body` are always sent; the remaining keys are
 * snake_cased and omitted when absent, preserving the legacy spread-conditional
 * shape exactly.
 *
 * @param options the export-asset options + the per-app `ENV.apiKey`
 *                (tio-data is owner-free — see auth-headers)
 */
export async function exportAsset(
  options: ExportAssetOptions & { apiKey: string }
): Promise<ExportAssetResponse> {
  const headers = buildTioAuthHeaders({
    apiKey: options.apiKey,
    contentType: 'application/json',
  });

  const response = await fetch(searchExportAssetUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      model_type: options.modelType,
      search_body: options.searchBody,
      ...(options.reportId ? { report_id: options.reportId } : {}),
      ...(options.companyId ? { company_id: options.companyId } : {}),
      ...(options.columnKeys ? { column_keys: options.columnKeys } : {}),
      ...(options.columnLabels ? { column_labels: options.columnLabels } : {}),
      ...(options.columnFormats ? { column_formats: options.columnFormats } : {}),
    }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      errors?: { detail?: string }[];
    };
    throw new Error(body.error || body.errors?.[0]?.detail || `Export failed (${response.status})`);
  }

  const result = (await response.json()) as ExportAssetResponse;

  // Trigger download from the signed asset URL
  const fileResponse = await fetch(result.asset.signedUrl);
  if (fileResponse.ok) {
    const blob = await fileResponse.blob();
    downloadFile(blob, result.filename, 'text/csv');
  }

  return result;
}
